import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { AddressSelectionDialogComponent } from '../dialogs/address-selection-dialog/address-selection-dialog.component';
import { CopyLinesDialogComponent } from '../dialogs/copy-lines-dialog/copy-lines-dialog.component';
import { EntityBreakDialogComponent } from '../dialogs/entity-break-dialog/entity-break-dialog.component';
import { EntityCancelDialogComponent } from '../dialogs/entity-cancel-dialog/entity-cancel-dialog.component';
import { LeadTimeDialogComponent } from '../dialogs/lead-time-dialog/lead-time-dialog.component';
import { LineSummaryDialogComponent } from '../dialogs/line-summary-dialog/line-summary-dialog.component';
import { RiserDialogComponent } from '../dialogs/riser-dialog/riser-dialog.component';
import { SalesRepDialogComponent } from '../dialogs/sales-rep-dialog/sales-rep-dialog.component';
import { TaggingDialogComponent } from '../dialogs/tagging-dialog/tagging-dialog.component';
import { WarrantyTextDialogComponent } from '../dialogs/warranty-text-dialog/warranty-text-dialog.component';
import { SkyportContactDialogComponent } from '../dialogs/skyport-contact-dialog/skyport-contact-dialog.component';
import { Entity, EntityKey } from '../entity/entity';
import { EntityLineHeader } from '../entity/entity-line-header';
import { PersonLogin, RoleName } from '../person/person-login';
import { SelectionItem } from '../selections/selection-item';
import { EntityService } from '../services/entity-service';
import { OrderService } from '../services/order-service';
import { OrganizationService } from '../services/organization-service';
import { PersonLoginService } from '../services/person-login-service';
import { PersonService } from '../services/person-service';
import { SelectionService } from '../services/selection-service';
import { SfrManagementService } from '../services/sfr-management-service';
import { DateUtils } from '../utils/date-utils';
import { StringUtils } from '../utils/string-utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProcessingStatusDialogComponent } from '../dialogs/processing-status-dialog/processing-status-dialog.component';
import { EntityComplexity } from '../model/entity-complexity';
import { EntityComplexityService } from '../services/entity-complexity.service';
import { ComplexityInfoDialogComponent } from '../dialogs/complexity-info-dialog/complexity-info-dialog.component';
import { ExtendedWarrantyDialogComponent } from '../dialogs/extended-warranty-dialog/extended-warranty-dialog.component';
import { ExtendedWarrantyUtils } from '../utils/extended-warranty-utils';
import { EntityEstimatedScheduleDialogComponent } from '../dialogs/entity-estimated-schedule-dialog/entity-estimated-schedule-dialog.component';
import { EstimatedReleaseScheduleService } from '../services/estimated-release-schedule.service';
import { SkyportValidationService } from '../services/skyport-validation.service';
import { LeadTimeUtils } from '../utils/lead-time-utils';
import { QewlDialogComponent } from '../dialogs/qewl-dialog/qewl-dialog.component';
import { DialogService } from '../services/dialog.service';
import { EnterContactDialogComponent } from '../dialogs/enter-contact-dialog/enter-contact-dialog.component';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IecTaskService } from '../services/iec-task-service';
import { IecTask } from '../tasks/iec-task';
import { EntityKeyUtils } from '../utils/entity-key-utils';
import { FpaService } from '../services/fpa-service';
import { UnrestrictedEntityUpdate } from '../model/unrestricted-entity-update';
import { IecSchedulingDataService } from '../services/iec-scheduling-data.service';
import { IecSchedulingData } from '../model/iec-scheduling-data';

@Component({
  selector: 'app-order-detail',
  templateUrl: './order-detail.component.html',
  styleUrls: ['./order-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButton,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    RouterLink,
    MatProgressSpinner,
    NgClass,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
  ],
})
export class OrderDetailComponent implements OnInit, OnChanges {
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly selectionService = inject(SelectionService);
  private readonly organizationService = inject(OrganizationService);
  private readonly entityService = inject(EntityService);
  private readonly schedulingDataService = inject(IecSchedulingDataService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly sfrService = inject(SfrManagementService);
  private readonly orderService = inject(OrderService);
  private readonly personService = inject(PersonService);
  private readonly entityComplexityService = inject(EntityComplexityService);
  private readonly estimatedScheduleService = inject(EstimatedReleaseScheduleService);
  private readonly skyportValidationService = inject(SkyportValidationService);
  private readonly dialogService = inject(DialogService);
  private readonly taskService = inject(IecTaskService);
  private readonly fpaService = inject(FpaService);
  private personLogin!: PersonLogin;
  private readonly canSubmitFpa = signal<boolean>(false);
  private readonly canCancelFpaReview = signal<boolean>(false);
  private readonly currentLeadTime = signal<string>('');
  readonly isRepUser = signal<boolean>(undefined!);
  controllingOrgs!: SelectionItem[];
  readonly sellingOrgDescription = signal<string>(undefined!);
  readonly shippingHandlingDescription = signal<string>(undefined!);
  readonly order = signal<Entity>(undefined!);
  readonly associatedOrders = signal<EntityKey[]>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly customers = signal<SelectionItem[]>(undefined!);
  readonly repOffices = signal<SelectionItem[]>(undefined!);
  readonly releaseTypes = signal<SelectionItem[]>(undefined!);
  readonly fobs = signal<SelectionItem[]>(undefined!);
  readonly paymentTerms = signal<SelectionItem[]>(undefined!);
  readonly salesReps = signal<SelectionItem[]>(undefined!);
  readonly outsideSalesReps = signal<SelectionItem[]>(undefined!);
  readonly iecContacts = signal<SelectionItem[]>(undefined!);
  readonly leadTimes = signal<SelectionItem[]>(undefined!);
  readonly salesChannels = signal<SelectionItem[]>(undefined!);
  readonly useTypes = signal<SelectionItem[]>(undefined!);
  readonly sAndHChargeTypes = signal<SelectionItem[]>(undefined!);
  readonly shipVias = signal<SelectionItem[]>(undefined!);
  readonly deliveryCountries = signal<SelectionItem[]>(undefined!);
  readonly marketSegments = signal<SelectionItem[]>(undefined!);
  readonly projectTypes = signal<SelectionItem[]>(undefined!);
  readonly specTypes = signal<SelectionItem[]>(undefined!);
  readonly milestones = signal<SelectionItem[]>(undefined!);
  readonly saveMessages = signal<string[]>(undefined!);
  selectedLines: EntityLineHeader[] = [];
  selectionStartIndex = -1;
  readonly createdBy = signal<string>(undefined!);
  readonly updatedBy = signal<string>(undefined!);
  readonly skyportContactMissing = signal<boolean>(false);
  readonly taggingRoomMissing = signal<boolean>(false);
  readonly mockupPending = signal<boolean>(true);
  readonly creditExceptionDescription = signal<string>(undefined!);
  readonly entityComplexity = signal<EntityComplexity>(undefined!);
  isConsolidatedView!: boolean;
  consolidatedLines!: EntityLineHeader[];
  readonly isRiserReviewTaskOpen = signal<boolean>(undefined!);
  readonly hasMissingRiserData = signal<boolean>(false);

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  generateConsolidatedLines(): void {
    if (this.order().lines && this.order().lines.length > 0 && !this.isConsolidatedView) {
      this.isConsolidatedView = true;
      this.consolidatedLines = structuredClone(this.order().lines);
      this.consolidatedLines = this.consolidatedLines.reduce(function (acc: EntityLineHeader[], curr: EntityLineHeader) {
        if (!acc.some((x) => x.product === curr.product && x.description === curr.description)) {
          acc.push(curr);
        } else {
          const foundElh: EntityLineHeader = acc.find((x) => x.product === curr.product && x.description === curr.description)!;
          foundElh.quantity = foundElh.quantity + curr.quantity;
        }
        return acc;
      }, []);
    } else {
      this.isConsolidatedView = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }

  private initialize(): void {
    this.loadSelectionLists();
    const idString: string = this.entityId();
    const revisionString: string = this.entityRevision();
    this.hasMissingRiserData.set(false);
    if (idString && idString.length > 0 && !isNaN(+revisionString)) {
      this.entityService.getEntity('CONTRACT', idString, +revisionString).subscribe({
        next: (result: Entity) => {
          this.taskService.getActiveTasksByNameAndEntity('Riser Data Review', idString, +revisionString).subscribe({
            next: (riserReviewTasks: IecTask[]) => {
              this.isRiserReviewTaskOpen.set((riserReviewTasks?.length || 0) > 0);
              this.order.set(result);
              this.checkForMissingRiserData();
              this.updateFpaStatusFlags();
              this.orderService.getReleases(this.order().id).subscribe((result) => (this.associatedOrders.set(result)));
              this.personService.getByUserLogin(this.order().createUser).subscribe((result) => {
                if (result) {
                  this.createdBy.set(result.description);
                } else {
                  this.createdBy.set(this.order().createUser);
                }
              });
              this.entityComplexityService.getEntityComplexity(this.order().id).subscribe((complexity) => (this.entityComplexity.set(complexity)));
              this.loadUpdateUser();
              this.loadSellingOrganizationSelections();
              this.orderService.getCreditExceptionDescription(this.order().creditExceptionCode).subscribe((creditException) => (this.creditExceptionDescription.set(creditException)));
              this.mockupPending.set(this.order().erpStatus === 'MocAppReq');
            },
            error: (error) => this.handleError(error),
          });
        },
        error: (error) => this.handleError(error),
      });
    } else {
      this.backToOrderMaster();
    }
  }

  private checkForMissingRiserData(): void {
    const orderId: EntityKey = this.order()?.id;
    if (orderId && StringUtils.isNotBlank(orderId.entityId)) {
      this.entityService.hasMissingRiserData(orderId).subscribe({
        next: (result: string) => {
          this.hasMissingRiserData.set(StringUtils.equalsIgnoreCase('true', result));
        },
        error: (error: any) => this.handleError(error),
      });
    }
  }

  private loadUpdateUser(): void {
    this.personService.getByUserLogin(this.order().updateUser).subscribe((result) => {
      if (result) {
        this.updatedBy.set(result.description);
      } else {
        this.updatedBy.set(this.order().updateUser);
      }
    });
  }

  save(): void {
    this.resetStatus();
    this.orderService.save(this.order()).subscribe({
      next: (result) => {
        this.order.set(result.updated);
        this.checkForMissingRiserData();
        this.isBusy.set(false);
        this.saveMessages.set(result.messages);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  submitForReview(): void {
    this.resetStatus();
    if ('MOK' === this.order().leadTimeCode && this.order().parentId != null) {
      this.order().parentId = new EntityKey();
      this.order().parentId.entityId = this.order().associatedOrderId;
      this.order().parentId.entityRevision = this.order().associatedOrderRevision;
    }
    if ('MR' === this.order().releaseTypeId) {
      this.estimatedScheduleService.getEstimatedScheduleError(this.order()).subscribe({
        next: (scheduleError: string) => {
          if (scheduleError && scheduleError.trim().length > 0) {
            this.viewEstimatedReleaseSchedule(scheduleError);
            this.isBusy.set(false);
          } else {
            this.skyportValidationService.getSkyportContactError(this.order()).subscribe({
              next: (skyportError: string) => {
                if (skyportError && skyportError.trim().length > 0) {
                  this.errorMessage.set(skyportError);
                  this.isBusy.set(false);
                } else {
                  this.proceedWithReview();
                }
              },
              error: (error) => this.handleError(error),
            });
          }
        },
        error: (error) => this.handleError(error),
      });
    } else {
      this.proceedWithReview();
    }
  }

  private proceedWithReview(): void {
    this.orderService.submitForReview(this.order().id).subscribe({
      next: (result) => {
        this.order.set(result);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
      complete: () => (this.isBusy.set(false)),
    });
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.saveMessages.set(undefined!);
    this.skyportContactMissing.set(false);
    this.taggingRoomMissing.set(false);
  }

  backToOrderMaster(): void {
    this.router.navigate(['orders']);
  }

  private loadSelectionLists(): void {
    this.selectionService.getControllingOrgs().subscribe((results) => (this.repOffices.set(results)));
    this.selectionService.getContractTypes().subscribe((results) => (this.releaseTypes.set(results)));
    this.selectionService.getDeliveryTerms().subscribe((results) => (this.fobs.set(results)));
    this.selectionService.getPaymentTerms().subscribe((results) => (this.paymentTerms.set(results)));
    this.selectionService.getCustomerServiceReps().subscribe((results) => (this.iecContacts.set(results)));
    this.selectionService.getFreightTerms().subscribe((results) => (this.shipVias.set(results)));
    this.selectionService.getCountryCodes().subscribe((results) => (this.deliveryCountries.set(results)));
    this.selectionService.getSalesAreas().subscribe((results) => (this.marketSegments.set(results)));
    this.selectionService.getRegions().subscribe((results) => (this.projectTypes.set(results)));
    this.selectionService.getNewExtendCodes().subscribe((results) => (this.specTypes.set(results)));
    this.selectionService.getMilestones().subscribe((results) => (this.milestones.set(results)));
  }

  loadSellingOrganizationSelections(): void {
    this.customers.set(undefined!);
    this.salesReps.set(undefined!);
    this.outsideSalesReps.set(undefined!);
    this.leadTimes.set(undefined!);
    this.salesChannels.set(undefined!);
    this.useTypes.set(undefined!);
    this.sAndHChargeTypes.set(undefined!);
    if (this.order() && this.order().repOfficeId) {
      this.isBusy.set(true);
      this.organizationService.getOrganization(this.order().repOfficeId).subscribe((sellingOrg) => {
        this.sellingOrgDescription.set(sellingOrg.description);
        this.shippingHandlingDescription.set(sellingOrg.transportationTermsDescription);
        this.selectionService.getCustomers(this.order().repOfficeId).subscribe((results) => {
          if (results.filter((result) => result.code == this.order().repOfficeId).length === 0) {
            const rep: SelectionItem = new SelectionItem();
            rep.code = sellingOrg.orgUnitId;
            rep.description = sellingOrg.description;
            results.unshift(rep);
          }
          this.customers.set(results);
        });
        this.selectionService.getOrganizationPeople(this.order().repOfficeId).subscribe({
          next: (results) => {
            this.salesReps.set(results);
            this.outsideSalesReps.set(results);
          },
        });
        this.selectionService.getOrganizationLeadTimes(this.order().repOfficeId).subscribe((results) => {
          this.leadTimes.set(results.filter((result) => !LeadTimeUtils.isPaperPilotLeadTime(result.code)));
        });
        this.selectionService.getOrganizationChannels(this.order().repOfficeId).subscribe((results) => {
          this.salesChannels.set(results);
        });
        this.selectionService.getOrganizationExpediteCodes(this.order().repOfficeId).subscribe((results) => {
          this.useTypes.set(results);
        });
        this.selectionService.getOrganizationTransportationTerms(this.order().repOfficeId).subscribe((results) => (this.sAndHChargeTypes.set(results)));
        this.currentLeadTime.set(this.order().leadTimeCode);
        this.isBusy.set(false);
      });
    }
  }

  userHasRole(roleName: string): boolean {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  isBypassUser(): boolean {
    return this.userHasRole('IECIT') || this.userHasRole('PartsEngineer');
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.personLogin = this.personLoginService.getCachedLogin()!;
      const primaryOrgUnit = this.personLogin.person.primaryOrgUnitId;
      if (primaryOrgUnit && primaryOrgUnit.length > 0) {
        this.organizationService.getOrganization(primaryOrgUnit).subscribe((organization) => {
          this.isRepUser.set(organization.typeCode == 'IREP' || organization.typeCode == 'OREP');
          this.initialize();
        });
      } else {
        this.isRepUser.set(false);
        this.initialize();
      }
    }
  }

  formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  setSfrPriceDate(newValue: Date) {
    this.order().sfrPriceBasisDate = DateUtils.formatOutput(newValue);
  }

  setShipDate(newValue: Date) {
    this.order().estimatedScheduleDate = DateUtils.formatOutput(newValue);
  }

  setPriceBasisDate(newValue: Date) {
    this.order().priceBasisDate = DateUtils.formatOutput(newValue);
  }

  setRequestedShipDate(newValue: Date) {
    this.order().requestedShipDate = DateUtils.formatOutput(newValue);
  }

  multiplier(): void {
    this.resetStatus();
    this.orderService.runMultiplier(this.order().id).subscribe({
      next: () => {
        this.entityService.getEntityByKey(this.order().id).subscribe({
          next: (result: Entity) => {
            this.order.set(result);
            this.isBusy.set(false);
            this.loadUpdateUser();
          },
          error: (error) => this.handleError(error),
        });
      },
      error: (error) => this.handleError(error),
    });
  }

  openConfigurator(): void {
    this.router.navigate(['configurator', this.order().id.entityName, this.order().id.entityId, this.order().id.entityRevision]);
  }

  private showRunMultiplierSnackBar(): void {
    if (!this.order().frozen && this.order().updateAllowed) {
      const snackBarRef = this.snackBar.open('Please run the multipler to see updated pricing', 'Run Now', {
        duration: 15000,
      });
      snackBarRef.onAction().subscribe(() => this.multiplier());
      snackBarRef.afterDismissed().subscribe((dismissed) => {
        if (!dismissed.dismissedByAction) {
          setTimeout(() => (this.saveMessages.set(new Array('Please run the multipler to see updated pricing'))), 0);
        }
      });
    }
  }

  addRisers(): void {
    const riserDialog = this.dialog.open(RiserDialogComponent, {
      disableClose: true,
      width: '1300px',
      height: '640px',
      data: { entity: this.order() },
    });
    riserDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.errorMessage.set(undefined!);
        this.order.set(result);
        this.showRunMultiplierSnackBar();
      }
    });
  }

  openLeadTimeDialog(): void {
    const leadTimeDialog = this.dialog.open(LeadTimeDialogComponent, {
      disableClose: true,
      data: { leadTimes: this.leadTimes(), entityKey: this.order().id },
    });
    leadTimeDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.errorMessage.set(undefined!);
        this.showRunMultiplierSnackBar();
        this.order().lines = result;
      }
    });
  }

  showLineSummary(): void {
    if (this.selectedLines && this.selectedLines.length === 1) {
      const selectedLine = this.selectedLines[0];
      const lineSummaryDialog = this.dialog.open(LineSummaryDialogComponent, {
        disableClose: true,
        data: { line: JSON.parse(JSON.stringify(selectedLine)), appEngineerSnBypass: this.isBypassUser() && this.order().appEngineerSnBypass },
      });
      lineSummaryDialog.afterClosed().subscribe((result) => {
        if (result) {
          this.isBusy.set(true);
          this.order().lines.filter((line) => line.lineId == result.lineId)[0].quantity = result.quantity;
          this.isBusy.set(false);
        }
      });
    }
  }

  selectLineRow(clickEvent: MouseEvent, line: EntityLineHeader): void {
    if (clickEvent.button !== 0) {
      return;
    }
    if (!clickEvent.ctrlKey && !clickEvent.shiftKey) {
      this.selectedLines = [];
      this.selectedLines.push(line);
      this.selectionStartIndex = this.order().lines.indexOf(line);
    } else {
      if (clickEvent.ctrlKey) {
        this.toggleLineSelection(line);
        this.selectionStartIndex = this.order().lines.indexOf(line);
      }
      if (clickEvent.shiftKey) {
        this.selectedLines = [];
        const clickedIndex = this.order().lines.indexOf(line);
        const first = Math.min(clickedIndex, this.selectionStartIndex);
        const last = Math.max(clickedIndex, this.selectionStartIndex);
        for (let index = first; index <= last; index++) {
          this.selectedLines.push(this.order().lines[index]);
        }
      }
    }
  }

  private toggleLineSelection(line: EntityLineHeader): void {
    const index = this.selectedLines.indexOf(line, 0);
    if (index > -1) {
      this.selectedLines.splice(index, 1);
    } else {
      this.selectedLines.push(line);
    }
  }

  isLineSelected(line: EntityLineHeader): boolean {
    return this.selectedLines.indexOf(line, 0) > -1;
  }

  getSelectedLineCount(): number {
    return this.selectedLines.length;
  }

  addParts(): void {
    this.dialogService.showPartsDialog(this.order()).subscribe({
      next: (result) => {
        if (result) {
          this.order.set(result);
          this.showRunMultiplierSnackBar();
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  copyLines(): void {
    const copyLinesDialog = this.dialog.open(CopyLinesDialogComponent, {
      disableClose: true,
      width: '1360px',
      height: '640px',
      data: { lines: JSON.parse(JSON.stringify(this.selectedLines)) },
    });
    copyLinesDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isBusy.set(true);
        this.router.navigate(['quotes', result.id.entityId, result.id.entityRevision]);
      }
    });
  }

  selectAddress(): void {
    const addressDialog = this.dialog.open(AddressSelectionDialogComponent, {
      data: { entity: JSON.parse(JSON.stringify(this.order())), personLogin: this.personLogin },
    });
    addressDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.order.set(result);
      }
    });
  }

  fpaManagement(): void {
    if (!this.errorMessage()) {
      this.resetStatus();
      if (this.hasMissingRiserData()) {
        this.errorMessage.set('Please upload risers via the Riser Chart button and ensure Project Defaults are completed before requesting a FPA.');
        this.isBusy.set(false);
      } else {
        if ('Approved' != this.order().approvalStatus && !this.order().frozen) {
          this.orderService.runMultiplier(this.order().id).subscribe({
            next: (updated: Entity) => {
              this.order.set(updated);
              if (LeadTimeUtils.isQuickShip(this.order().leadTimeCode)) {
                this.errorMessage.set(LeadTimeUtils.QUICK_SHIP_FPA_MESSAGE);
                this.isBusy.set(false);
              } else {
                this.router.navigate(['fpa/CONTRACT/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
              }
            },
            error: (error) => this.handleError(error),
          });
        } else if (LeadTimeUtils.isQuickShip(this.order().leadTimeCode)) {
          this.errorMessage.set(LeadTimeUtils.QUICK_SHIP_FPA_MESSAGE);
          this.isBusy.set(false);
        } else {
          this.router.navigate(['fpa/CONTRACT/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
        }
      }
    }
  }

  viewQuote(): void {
    this.router.navigate(['quotes', this.order().associatedQuoteId, this.order().associatedQuoteRevision]);
  }

  viewBlanket(): void {
    this.router.navigate(['orders', this.order().associatedOrderId, this.order().associatedOrderRevision]);
  }

  viewOrder(id: EntityKey): void {
    this.router.navigate(['orders', id.entityId, id.entityRevision]);
  }

  viewDocuments(): void {
    this.router.navigate(['documents/' + this.order().id.entityName + '/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
  }

  viewReleaseSummary(): void {
    this.router.navigate(['releaseSummary/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
  }

  viewEntityBreaks(): void {
    const breaksDialog = this.dialog.open(EntityBreakDialogComponent, {
      data: { entityKey: this.order().id, personLogin: this.personLogin },
    });
    breaksDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.showRunMultiplierSnackBar();
      }
    });
  }

  cancelSelectedLines(): void {
    const cancelDialog = this.dialog.open(EntityCancelDialogComponent, {
      data: { entityKey: this.order().id, entityLines: this.selectedLines },
    });
    cancelDialog.afterClosed().subscribe((cancelReason) => {
      if (cancelReason) {
        this.isBusy.set(true);
        this.orderService.cancelLines(cancelReason, this.selectedLines).subscribe({
          next: (updated) => {
            this.order.set(updated);
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  showSalesRep(): void {
    if (this.order().salesRepId && this.order().salesRepId.length > 0) {
      this.personService.getContactPerson(this.order().salesRepId).subscribe((result) => {
        const salesRep = result;
        if (salesRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '400px',
            height: '150px',
            data: { rep: salesRep },
          });
        }
      });
    }
  }

  showIecContact(): void {
    if (this.order().iecContactId && this.order().iecContactId.length > 0) {
      this.personService.getContactPerson(this.order().iecContactId).subscribe((result) => {
        const salesRep = result;
        if (salesRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '400px',
            height: '150px',
            data: { rep: salesRep },
          });
        }
      });
    }
  }

  showWarrantyText(): void {
    this.dialog.open(WarrantyTextDialogComponent, {
      disableClose: true,
      width: '400px',
      height: '250px',
      data: { warrantyText: this.order().warrantyRequestNotes },
    });
  }

  refreshIecContact(): void {
    this.currentLeadTime.set(this.order().leadTimeCode);
  }

  selectSkyportContact(): void {
    this.entityService.hasSkyportCharacteristic(this.order().id).subscribe((result) => {
      if (result && result == 'true') {
        this.orderService.save(this.order()).subscribe({
          next: (result) => {
            this.order.set(result.updated);
            this.saveMessages.set(result.messages);
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
        this.showSkyportContactDialog();
      }
    });
  }

  private showSkyportContactDialog(): void {
    const skyportDialog = this.dialog.open(SkyportContactDialogComponent, {
      data: { entity: JSON.parse(JSON.stringify(this.order())) },
      width: '550px',
      height: '380px',
    });
    skyportDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.order.set(result);
      }
    });
  }

  cancel(): void {
    const messages: string[] = [];
    messages.push('This action will cancel this order.');
    messages.push('');
    messages.push('Do you want to proceed?');
    this.dialogService.showConfirmationDialog(messages, 'Yes', 'No').subscribe({
      next: (response: boolean) => {
        if (response) {
          this.resetStatus();
          this.isBusy.set(true);
          this.orderService.cancel(this.order().id).subscribe({
            next: (result: Entity) => {
              this.order.set(result);
              this.isBusy.set(false);
              this.loadUpdateUser();
            },
            error: (error) => this.handleError(error),
          });
        }
      },
    });
  }

  fixPermissions(): void {
    this.resetStatus();
    this.entityService.fixPermissions(this.order().id).subscribe({
      next: () => (this.isBusy.set(false)),
      error: (error) => this.handleError(error),
    });
  }

  canSubmitSfrs(): boolean {
    return this.order().updateAllowed && this.isBlanket();
  }

  private statusIn(codes: string[]): boolean {
    return codes.indexOf(this.order().statusDescription, 0) > -1;
  }

  canCancelSfrs(): boolean {
    const applicableCodes: string[] = ['SFR Review'];
    return this.order().updateAllowed && this.statusIn(applicableCodes);
  }

  sfrManagement(): void {
    this.router.navigate(['sfrManagement/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
  }

  submitSfr(): void {
    if (!this.errorMessage()) {
      this.resetStatus();
      this.sfrService.createRequest(this.order().id).subscribe({
        next: (result) => {
          this.order.set(result);
          this.isBusy.set(false);
          this.loadUpdateUser();
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  cancelSfr(): void {
    this.resetStatus();
    this.sfrService.cancelRequest(this.order().id).subscribe({
      next: (result) => {
        this.order.set(result);
        this.isBusy.set(false);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  generateRevision(): void {
    const messages: string[] = [];
    messages.push('This action will generate a revision to this order.');
    messages.push('');
    messages.push('Do you want to proceed?');
    this.dialogService.showConfirmationDialog(messages, 'Yes', 'No').subscribe({
      next: (response: boolean) => {
        if (response) {
          this.resetStatus();
          this.orderService.generateRevision(this.order().id).subscribe({
            next: (result) => {
              this.viewOrder(result.id);
            },
            error: (error) => this.handleError(error),
          });
        }
      },
    });
  }

  isBlanket(): boolean {
    const blanketPattern = /^(?:MR|SR)\d{6}$/g;
    return RegExp(blanketPattern).test(this.order().id.entityId);
  }

  showIecRelease(): boolean {
    const validApprovals: string[] = ['Pending Release', 'CSR Review'];
    return validApprovals.indexOf(this.order().approvalStatus, 0) > -1 && this.userCanReview();
  }

  showExpediteParts(): boolean {
    return LeadTimeUtils.isPartsOrder(this.order().leadTimeCode);
  }

  releasePendingApproval(): void {
    this.resetStatus();
    this.orderService.releasePendingApproval(this.order().id).subscribe({
      next: (result) => {
        this.isBusy.set(false);
        this.order.set(result);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  userCanReview(): boolean {
    switch (this.order().reviewStatus) {
      case 'Admin Review':
        return this.personLoginService.hasRole(RoleName.IECIT);
      case 'Assign Folder Number':
      case 'SABR Review':
        return this.personLoginService.hasRole(RoleName.IECIT) || this.personLoginService.hasRole(RoleName.IECCredAnalyst);
      case 'CSR Review':
      case 'Valid':
        return this.isCustomerServiceOrIT();
      default:
        return false;
    }
  }

  isCustomerServiceOrIT() {
    return this.personLoginService.hasRole(RoleName.IECIT) || this.personLoginService.hasRole(RoleName.IECCustomerService);
  }

  canRelease(): boolean {
    const validApprovals: string[] = ['Approved - Need Releases', 'Approved'];
    return this.isBlanket() && this.order().statusDescription != 'Subsequent Revision' && validApprovals.indexOf(this.order().approvalStatus, 0) > -1;
  }

  readyForScheduling(): boolean {
    return !this.isBlanket() && 'BOOKED' == this.order().milestone && 'Approved' == this.order().approvalStatus && this.isCustomerServiceOrIT();
  }

  sendToScheduling(): void {
    this.resetStatus();
    this.orderService.sendToScheduling(this.order().id).subscribe({
      next: (result) => {
        this.isBusy.set(false);
        this.saveMessages.set([result]);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  isAcknowledgmentReady(): boolean {
    const earliest: Date = new Date(1900, 0, 1);
    const scheduleDate: Date = new Date(this.order().estimatedScheduleDate);
    return 'Approved' == this.order().approvalStatus && earliest < scheduleDate;
  }

  sendAcknowledgment(): void {
    this.resetStatus();
    this.orderService.sendAcknowledgment(this.order().id).subscribe({
      next: (result) => {
        this.isBusy.set(false);
        this.saveMessages.set([result]);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  generateRelease(): void {
    this.router.navigate(['orderRelease/' + this.order().id.entityId + '/' + this.order().id.entityRevision]);
  }

  isOrderReviewable(): boolean {
    const invalidStatuses: string[] = ['Cancelled', 'Closed', 'Subsequent Revision'];
    return this.order().updateAllowed && invalidStatuses.indexOf(this.order().statusDescription) === -1 && 'Pending Release' == this.order().approvalStatus;
  }

  openTaggingDialog(): void {
    const taggingDialog = this.dialog.open(TaggingDialogComponent, {
      disableClose: true,
      width: '800px',
      maxWidth: '90vw',
      height: '400px',
      data: { entityKey: this.order().id },
    });
    taggingDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isBusy.set(true);
        this.entityService.getEntityByKey(this.order().id).subscribe({
          next: (result) => {
            this.order.set(result);
            this.checkForMissingRiserData();
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  openProjectDefaultsScreen(): void {
    this.router.navigate(['projectDefaults', this.order().id.entityName, this.order().id.entityId, this.order().id.entityRevision]);
  }

  openRiserChartDialog(): void {
    this.router.navigate(['riserChart', this.order().id.entityName, this.order().id.entityId, this.order().id.entityRevision]);
  }

  canAddLines(): boolean {
    return this.order() && 'OR' != this.order().releaseTypeId && !this.order().frozen && this.order().approvalStatus.indexOf('Approved') < 0;
  }

  viewProcessingStatus(): void {
    this.dialog.open(ProcessingStatusDialogComponent, {
      width: '70vw',
      height: '300px',
      data: { entityKey: this.order().id },
    });
  }

  getReleaseType(): string {
    const filtered = this.releaseTypes().filter((releaseType) => releaseType.code === this.order().releaseTypeId);
    return filtered && filtered.length === 1 ? filtered[0].description : 'Unknown';
  }

  getComplexityCategory(): string {
    return this.isComplexityCategoryAssigned() ? this.entityComplexity().complexityCategory.category : 'Unknown';
  }

  showComplexityCategory(): boolean {
    return !this.entityService.isPartsOrWarranty(this.order());
  }

  private isComplexityCategoryAssigned(): boolean {
    return !!(
      this.entityComplexity() &&
      this.entityComplexity().complexityCategory &&
      this.entityComplexity().complexityCategory.category &&
      this.entityComplexity().complexityCategory.category.trim().length > 0
    );
  }

  getComplexityLeadTime(): string {
    return this.isComplexityCategoryAssigned() ? this.entityComplexity().complexityCategory.leadTimeWeeks + ' weeks' : 'Unknown';
  }

  updateEntityComplexity(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.entityComplexityService.updateEntityComplexity(this.order().id).subscribe({
      next: (complexity) => {
        this.isBusy.set(false);
        this.entityComplexity.set(complexity);
      },
      error: (error) => this.handleError(error),
    });
  }

  showComplexityInformationDialog(): void {
    this.dialog.open(ComplexityInfoDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      height: '300px',
      data: { entity: this.order(), complexity: this.entityComplexity() },
    });
  }

  canUpdateExtendedWarranty(): boolean {
    return ExtendedWarrantyUtils.canWarrantyBeChanged(this.order());
  }

  showExtendedWarrantyDialog(): void {
    const warrantyDialog = this.dialog.open(ExtendedWarrantyDialogComponent, {
      disableClose: true,
      width: '800px',
      maxWidth: '90vw',
      height: '400px',
      data: { entity: this.order() },
    });
    warrantyDialog.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.isBusy.set(true);
          this.entityService.getEntityByKey(this.order().id).subscribe({
            next: (entity) => {
              this.order.set(entity);
              this.isBusy.set(false);
            },
            error: (error) => this.handleError(error),
          });
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  viewEstimatedReleaseSchedule(warningMessage: string): void {
    this.dialog.open(EntityEstimatedScheduleDialogComponent, {
      width: '1200px',
      maxWidth: '90vw',
      height: '600px',
      data: { entity: this.order(), warningMessage: warningMessage },
    });
  }

  openQewlDialog(): void {
    this.dialog.open(QewlDialogComponent, {
      width: '1360px',
      height: '650px',
      data: this.order(),
    });
  }

  openCostReport(): void {
    this.router.navigate(['entityCostReport', this.order().id.entityId, this.order().id.entityRevision]);
  }

  openAutoCostReport(): void {
    this.router.navigate(['autoCostReport', this.order().id.entityId, this.order().id.entityRevision]);
  }

  isInternalOrRsm(): boolean {
    return this.personLoginService.isInternalOrRsm();
  }

  openSureFlowLoopSimulator(): void {
    this.router.navigate(['orders', 'sureflow', 'loops', this.order().id.entityId, this.order().id.entityRevision]);
  }

  isSureFlowUnit(line: EntityLineHeader): boolean {
    const productId = line?.product || '';
    return productId.toUpperCase().indexOf('SUREFLOW') >= 0;
  }

  isSureFlowUnitSelected(): boolean {
    if (!this.selectedLines || this.selectedLines.length === 0) {
      return false;
    }
    return this.selectedLines.filter((line) => this.isSureFlowUnit(line)).length > 0;
  }

  enterContact(isPrimaryContact: boolean): void {
    const dialog = this.dialog.open(EnterContactDialogComponent, {
      data: { isPrimaryContact: isPrimaryContact, entity: this.order() },
    });
    dialog.afterClosed().subscribe((result) => {
      if (result) {
        if (result instanceof HttpErrorResponse) {
          this.handleError(result);
        } else {
          this.order.set(result);
        }
      }
    });
  }

  canSubmitForReview(): boolean {
    return this.isOrderReviewable() && !this.isRiserReviewTaskOpen();
  }

  getSubmitForReviewTooltip(): string {
    if (this.isRiserReviewTaskOpen()) {
      return 'The riser data on this order is currently being reviewed by Application Engineering and cannot be resubmitted until that review is completed.';
    }
    return '';
  }

  showEntityHistoryDialog(): void {
    if (this.order()) {
      this.dialogService.showEntityHistoryDialog(this.order());
    }
  }

  showFpaManagementButton(): boolean {
    return this.canSubmitFpa();
  }

  showFpaCancelButton(): boolean {
    return this.canCancelFpaReview();
  }

  private updateFpaStatusFlags(): void {
    this.canCancelFpaReview.set(false);
    this.canSubmitFpa.set(false);
    if (StringUtils.isNotBlank(this.order()?.id?.entityId)) {
      const fpaStatus: string = this.order()?.fpaStatus || 'None';
      this.fpaService.isPendingApproval(fpaStatus).subscribe({
        next: (response: string) => {
          this.canCancelFpaReview.set(StringUtils.equalsIgnoreCase('true', response));
          this.canSubmitFpa.set(!this.order().frozen && !this.canCancelFpaReview());
        },
        error: (error: any) => this.handleError(error),
      });
    }
  }

  cancelFpaReview(): void {
    this.isBusy.set(true);
    this.fpaService.cancelSubmission(EntityKeyUtils.convertToFullEntityKey(this.order().id)).subscribe({
      next: () => {
        this.entityService.getEntityByKey(this.order().id).subscribe({
          next: (result: Entity) => {
            this.order.set(result);
            this.isBusy.set(false);
            this.updateFpaStatusFlags();
          },
          error: (error) => this.handleError(error),
        });
      },
      error: (error: any) => this.handleError(error),
    });
  }

  canCopyLines(): boolean {
    return !this.isBusy() && this.getSelectedLineCount() > 0 && this.canCopyAllSelectedLines();
  }

  private canCopyAllSelectedLines(): boolean {
    if ((this.selectedLines?.length || 0) === 0) {
      return false;
    }
    for (const line of this.selectedLines) {
      if (!line.copyAllowed) {
        return false;
      }
    }
    return true;
  }

  showUnrestrictedEditOption(): boolean {
    return this.order() && this.order().updateAllowed && this.order().frozen && this.canUpdateInternalNotes();
  }

  canUpdateInternalNotes(): boolean {
    return this.userHasRole("IECCustomerService") || this.userHasRole("IECIT") || this.userHasRole("IECCredAnalyst") || this.userHasRole("IECApplicationEngineer") || this.userHasRole("IECRSM");
  }

  showUnrestrictedChangesDialog(): void {
    if (this.showUnrestrictedEditOption()) {
      this.dialogService.showUnrestrictedEntityUpdatesDialog(this.order()).subscribe({
        next: (update: UnrestrictedEntityUpdate) => {
          if (update) {
            this.isBusy.set(true);
            this.errorMessage.set(undefined!);
            this.entityService.saveEntityWithoutRestrictions(update).subscribe({
              next: (updatedEntity: Entity) => {
                this.order.set(updatedEntity);
                this.isBusy.set(false);
              }, error: (error: any) => this.handleError(error)
            });
          }
        }, error: (error: any) => this.handleError(error)
      });
    }
  }

  showRiserChartButton(): boolean {
    return this.isAdminOrCustomerServiceOrBetaTester();
  }

  private isAdminOrCustomerServiceOrBetaTester(): boolean {
    return this.isAdminOrCustomerService() || this.personLoginService.isBetaTester();
  }

  private isAdminOrCustomerService(): boolean {
    return this.userHasRole('IECIT') || this.userHasRole('IECCustomerService');
  }

  showProjectDefaultsButton(): boolean {
    return this.order().updateAllowed && this.isAdminOrCustomerServiceOrBetaTester();
  }

  showSchedulingDataButton(): boolean {
    return !this.isBlanket() && this.isAdminOrCustomerService();
  }

  viewSchedulingData(): void {
    this.schedulingDataService.getSchedulingData(this.order().id.entityId, this.order().id.entityRevision).subscribe({
      next: (schedulingData: IecSchedulingData) => {
        this.dialogService.showSchedulingDataDialog(schedulingData);
      }, error: (error) => this.handleError(error)
    });
  }

}
